import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { RootState } from "../store";

export interface TenantBranding {
  logo?: string;
  backgroundImage?: string;
  primaryColor?: string;
}

interface TenantSettings {
  allowedStates?: string[];
}

export interface TenantInfo {
  _id: string;
  name: string;
  companyName?: string;
  slug: string;
  branding?: TenantBranding;
  settings?: TenantSettings;
  status?: string;
  subscriptionTier?: string;
  maxUsers?: number;
  owner?: {
    email: string;
  };
  createdAt?: string;
  updatedAt?: string;
}

const baseQuery = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_API_BASE_URL,
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as RootState).auth.token;
    if (token) headers.set("Authorization", `Bearer ${token}`);
    return headers;
  },
});

export const tenantSlice = createApi({
  reducerPath: "tenantSlice",
  baseQuery,
  tagTypes: ["Tenant", "TenantBranding"],
  // keep data cached for 10 minutes - tenant info doesn't change often
  keepUnusedDataFor: 600,
  refetchOnMountOrArgChange: 60, // only refetch if older than 60 seconds
  refetchOnFocus: false, // don't refetch just because window gets focus
  refetchOnReconnect: true, // do refetch when reconnecting
  endpoints: (builder) => ({
    // Public endpoint for tenant info (no auth required)
    getTenantBySlug: builder.query<TenantInfo, string>({
      query: (slug) => `/tenants/public/${slug}`,
      providesTags: (_result, _error, slug) => [{ type: "Tenant", id: slug }],
    }),

    // Authenticated tenant endpoints
    getTenantSettings: builder.query<TenantInfo, string>({
      query: (slug) => `/tenants/${slug}`,
      providesTags: (_result, _error, slug) => [{ type: "Tenant", id: slug }],
    }),

    getAllTenants: builder.query<
      { tenants: TenantInfo[]; count: number },
      void
    >({
      query: () => "/tenants",
      providesTags: (result) =>
        result
          ? [
              ...result.tenants.map(({ _id }) => ({
                type: "Tenant" as const,
                id: _id,
              })),
              { type: "Tenant", id: "LIST" },
            ]
          : [{ type: "Tenant", id: "LIST" }],
    }),

    getTenantById: builder.query<TenantInfo, string>({
      query: (id) => `/tenants/${id}`,
      providesTags: (_result, _error, id) => [{ type: "Tenant", id }],
    }),

    createTenant: builder.mutation<TenantInfo, Partial<TenantInfo>>({
      query: (tenant) => ({
        url: "/tenants",
        method: "POST",
        body: tenant,
      }),
      invalidatesTags: [{ type: "Tenant", id: "LIST" }],
    }),

    updateTenant: builder.mutation<
      TenantInfo,
      { id: string; tenant: Partial<TenantInfo> }
    >({
      query: ({ id, tenant }) => ({
        url: `/tenants/${id}`,
        method: "PUT",
        body: tenant,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "Tenant", id },
        { type: "Tenant", id: "LIST" },
      ],
    }),

    deleteTenant: builder.mutation<void, string>({
      query: (id) => ({
        url: `/tenants/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "Tenant", id: "LIST" }],
    }),

    // Tenant branding endpoints
    getTenantBranding: builder.query<TenantBranding, string>({
      query: (tenantSlug) => `/tenants/${tenantSlug}/branding`,
      transformResponse: (response: { branding: TenantBranding }) =>
        response.branding,
      providesTags: (_result, _error, slug) => [
        { type: "TenantBranding", id: slug },
      ],
    }),

    updateTenantBranding: builder.mutation<
      TenantBranding,
      { tenantSlug: string; branding: Partial<TenantBranding> }
    >({
      query: ({ tenantSlug, branding }) => ({
        url: `/tenants/${tenantSlug}/branding`,
        method: "PUT",
        body: branding,
      }),
      transformResponse: (response: {
        message: string;
        branding: TenantBranding;
      }) => response.branding,
      invalidatesTags: (_result, _error, { tenantSlug }) => [
        { type: "TenantBranding", id: tenantSlug },
        { type: "Tenant", id: tenantSlug },
      ],
    }),

    getTenantInviteToken: builder.query<{ token: string }, string>({
      query: (tenantSlug) => `/tenants/${tenantSlug}/invite-token`,
    }),

    generateTenantInviteToken: builder.mutation<{ token: string }, string>({
      query: (tenantSlug) => ({
        url: `/tenants/${tenantSlug}/invite-token`,
        method: "POST",
      }),
    }),

    validateInviteToken: builder.query<
      { valid: boolean; tenantName: string },
      string
    >({
      query: (token) => `/tenants/validate-invite-token/${token}`,
    }),
  }),
});

export const {
  useGetTenantBySlugQuery,
  useGetTenantSettingsQuery,
  useGetAllTenantsQuery,
  useGetTenantByIdQuery,
  useCreateTenantMutation,
  useUpdateTenantMutation,
  useDeleteTenantMutation,
  useGetTenantBrandingQuery,
  useUpdateTenantBrandingMutation,
  useGetTenantInviteTokenQuery,
  useGenerateTenantInviteTokenMutation,
  useValidateInviteTokenQuery,
  // export prefetch methods for manual prefetching
  usePrefetch,
} = tenantSlice;
